// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.FieldContainerApi.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
// </copyright>
// ----------------------------------------------------------------------

import fieldContainerFactories from 'share/factories/Izenda.ReportPartFieldContainerFactories';
import {
  createContainerSchema,
  createStaticLabelSchema,
  createConditionalContainerSchema
} from 'modules/ReportDesigner/Fields/ReportBody/ReportPartType/FieldContainers/Izenda.FieldContainerSchemaFunctions';

export default {
  /**
   * Register field container with its factory function in order to create a custom field
   * container in Report Designer's report part configuration. Most of the time, using createFieldContainerSchema function
   * to create labeled box of fields fits in the same way with current Values, Labels... field containers.
   * @param {string} type Field container type, i.e. chart_label, grid_rows...
   * @param {Function} factory Function to create field container
   * @example <caption>Create an custom metric fields container using CustomMetricReactComponent</caption>
   *  // Note that it requires to create a React component to render the custom metric GUI
   *  registerFieldContainer('custom_metric', function (type, containerProps){
   *    return {
   *      key: type,
   *      component: CustomMetricReactComponent,
   *      props : containerProps
   *    }
   *  });
   */
  registerFieldContainer(type: string, factory: (type: string, containerProps: Object) => Object): void {
    fieldContainerFactories.containKey(type) && fieldContainerFactories.unregister(type);
    fieldContainerFactories.register(type, factory);
  },

  /**
   * Create a label and an input box of fields which allows to add/remove or drag/drop a field into.
   * @param {string} containerKey An unique container key to identify which container is
   * @param {string|Function} label The label text value or a function return a text
   * @param {string} dataKey Data key to access container information of report part content's property
   * @param {int} height Pixel height of container. Leave it null or undefined to use default height.
   * @param {int} maximumField Maximum number of fields in the container. Leave it null or undefined to unlimit the number of fields
   * @param {boolean} [isHorizontal=false] Is horizontal alignment between label and input box. Default value is false.
   * @example <caption>Create Z-Axis Values field container with dataKey is ZValues, having default height and 1 field maximum</caption>
   *  createFieldContainerSchema('ZValues', 'Z-Axis Values', 'ZValues', null, 1)
   */
  createFieldContainerSchema: createContainerSchema,

  /**
   * Create a static label only without input box
   * @param {string} key The unique container key
   * @param {string|Function} label The label text value or a function return a text
   * @example
   *  createStaticLabelSchema('LabelPoint', () => localizer.getText('LABEL_POINT_OPTIONS'))
   */
  createStaticLabelFieldContainerSchema: createStaticLabelSchema,

  /**
   * Create a conditional label and a input box of fields which allows to add/remove or drag/drop a field into.
   * @param {Function} predicateFn A predicate to determine whether render the container or not. It receives report part detail object as a parameter.
   * @param {string} containerKey key
   * @param {string|Function} label The label text value or a function return a text
   * @param {string} dataKey Data key to access container information of report part content's property
   * @param {int} height Pixel height of container. Leave it null or undefined to use default height.
   * @param {int} maximumField Maximum number of fields in the container. Leave it null or undefined to unlimit the number of fields
   * @param {boolean} [isHorizontal=false] Is horizontal alignment between label and input box. Default value is false.
   * @example <caption>Create an example container with dataKey is exampleContainer, having default height and 1 field maximum</caption>
   *  createConditionalContainerSchema(
   *    function(reportPartDetail) {
   *      return true;
   *    },
   *    'exampleContainer',
   *    () => localizer.getText('ExampleContainer'),
   *    'exampleContainer',
   *    null,
   *    1,
   *    true
   *  )
   */
  createConditionalFieldContainerSchema: createConditionalContainerSchema
};



// WEBPACK FOOTER //
// ./ui/api/Izenda.FieldContainerApi.js